/* eslint-disable no-console */
import dayjs from '../dayjsConfig'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { push } from 'connected-react-router'
import { t } from 'ttag'

import api from './api.services'
import {
  ActivityType,
  AppleHealthDiaryEntry,
  OnboardingStatus,
  ProgressType,
  ServiceProvider,
  UserProfileDto,
} from './api.types'
import {
  AppleHealthDataTypes,
  GoogleFitDataTypes,
  HealthConnectDataTypes,
} from '../common/integration/integrationSagas'
import { store } from '../index'
import { log } from './log'
import { clearNotification } from '../common/notifications/NotificationActions'
import { isNative } from '../common/helpers/isNative'
import { ROUTES } from '../common/Routes/routePaths'
import { updateLastSync } from '../common/auth/authActions'
import { getUserCompanies, getUserDto, getUserIntegrationScopes } from '../common/auth/authSelectors'
import { GoogleFit } from './GoogleFit'
import { ActivityContainer, HistoryActivityData } from './GoogleFitDefinitions'
import { convertUTCToLocalTime } from '../common/helpers/date'
import { HealthConnect } from './HealthConnect'
import { IGlobalState } from '../Root/store/appReducer'
import { Dialog } from '@capacitor/dialog'
import { hasFeatureFlag } from '../common/helpers/featureFlags'

/**
 * Clears notifications, then triggers an update of last sync time
 * (and potentially triggers an update on certain views).
 */
export const refreshActivityView = (): void => {
  store.dispatch(clearNotification())
  // If you intend to refresh the user profile or a company goal,
  // dispatch those actions here as needed.

  // For now, just update the last sync time.
  store.dispatch(updateLastSync())
}

function calculateDurationInSeconds(date1: Date, date2: Date): number {
  // Get the time difference in milliseconds
  const timeDifference = date2.getTime() - date1.getTime()

  // Convert milliseconds to seconds
  return Math.floor(timeDifference / 1000)
}

/**
 * Handle the new Google Fit scope changes. If `integrationError` starts with 'GOOGLE_FIT:',
 * prompt the user to grant full scope.
 */
const handleGoogleFitScopeChange = (message: string): void => {
  const msg = message.replace(/^GOOGLE_FIT:\s*/, '').trim()
  if (!msg) return

  const globalState = store.getState() as unknown as IGlobalState
  const path = globalState?.router?.location?.pathname?.split('/') ?? []
  // Skip if we're already on the integration proxy
  if (path[1] === 'integration-proxy') return

  if (!isNative()) {
    // Web browser
    if (window.confirm(msg)) {
      store.dispatch(push(`${ROUTES.INTEGRATION_PROXY.to}?type=${ServiceProvider.GOOGLE_FIT}&scope=FULL`))
    }
  } else {
    // On device
    // @ts-ignore - if you have a type for `navigator.notification.confirm`, use it here
    navigator.notification.confirm(
      msg,
      (button: number) => {
        if (button === 1) return
        store.dispatch(push(`${ROUTES.INTEGRATION_PROXY.to}?type=${ServiceProvider.GOOGLE_FIT}&scope=FULL`))
      },
      'Google Fit',
      [t`Cancel`, t`OK`],
    )
  }
}

/**
 * Run a data sync for Apple Health / Google Fit / Health Connect, depending on user's `serviceProvider`.
 *
 * @param manualSync    Whether this sync is triggered manually by the user (vs. background sync).
 * @param forceLongSync Forces a longer sync range from the backend if `true`.
 * @param nonDistanceSync If `true`, skip the distance/steps portion of the sync.
 */
export const dataSync = async (
  manualSync?: boolean,
  forceLongSync = false,
  nonDistanceSync?: boolean,
): Promise<void> => {
  try {
    const state = store.getState() as unknown as IGlobalState
    const userDto = await getUserDto(state)
    if (!userDto) return

    const {
      userId,
      serviceProvider,
      onboardingStatus,
      // integrationError,
      dateOfBirth,
      allowManualEntry,
    }: UserProfileDto = userDto

    // If no userId, nothing to sync.
    if (!userId) return

    // If not on a native device OR user isn't fully onboarded (and not forcing a sync),
    // we skip to avoid errors.
    if (
      !isNative() ||
      (onboardingStatus !== OnboardingStatus.DONE && !manualSync && !forceLongSync && !nonDistanceSync)
    ) {
      return
    }

    // Early return if platform mismatch vs. service provider
    if (
      (Capacitor.getPlatform() === 'ios' && serviceProvider !== ServiceProvider.APPLE_HEALTH) ||
      (Capacitor.getPlatform() === 'android' &&
        !forceLongSync &&
        serviceProvider !== ServiceProvider.GOOGLE_FIT &&
        serviceProvider !== ServiceProvider.HEALTH_CONNECT)
    ) {
      return
    }

    const isGoogleFit = serviceProvider === ServiceProvider.GOOGLE_FIT
    const isAppleHealth = serviceProvider === ServiceProvider.APPLE_HEALTH
    const isHealthConnect = serviceProvider === ServiceProvider.HEALTH_CONNECT

    // Throttle logic so we don't sync too frequently
    const throttleLimitMs = nonDistanceSync ? 0 : manualSync ? 5000 : 30000
    const timestamp = Date.now()
    const { value: lastPushRaw } = await Preferences.get({ key: 'lastAppleHealthPush' })
    const lastPushTimestamp = parseInt(lastPushRaw ?? '0', 10) || 0
    const syncThrottlePassed = lastPushTimestamp < timestamp - throttleLimitMs
    const lastPushWasInFuture = lastPushTimestamp > timestamp + 1000
    const secondsSinceLastPush = Math.round((timestamp - lastPushTimestamp) / 1000)

    if (lastPushTimestamp && !syncThrottlePassed && !lastPushWasInFuture) {
      log(
        `Skipping ${serviceProvider} sync – last one was done just ${secondsSinceLastPush} seconds ago. ${
          manualSync ? '(manual sync)' : ''
        }`,
      )
      return
    }

    // Otherwise proceed
    await Preferences.set({ key: 'lastAppleHealthPush', value: String(timestamp) })
    const syncStart = window.performance.now()
    const needsFullScopeSync = !!dateOfBirth

    log(
      `Starting ${serviceProvider} sync – ${secondsSinceLastPush} seconds has passed. ${
        manualSync ? '(manual sync)' : ''
      } | ${
        manualSync ? '(manual sync)' : ''
      } | needsFullScopeSync: ${needsFullScopeSync ? 'YES' : 'NO'} | nonDistanceSync: ${
        nonDistanceSync ? 'YES' : 'NO'
      } | allowManualEntry: ${allowManualEntry ? 'YES' : 'NO'}`,
    )

    // Get the data types we want to sync
    const health = isGoogleFit ? GoogleFit : cordova.plugins.health
    let healthDataTypes = isAppleHealth
      ? AppleHealthDataTypes
      : isHealthConnect
        ? HealthConnectDataTypes
        : GoogleFitDataTypes

    // Check availability
    if (isHealthConnect) {
      let isHealthConnectAvailable = false
      try {
        await new Promise<void>((resolve, reject) => {
          health.isAvailable(
            (isAvailable: boolean) => {
              console.log('Health Connect isAvailable:', isAvailable)
              if (isAvailable) {
                isHealthConnectAvailable = true
              }
              resolve()
            },
            (error: unknown) => {
              reject(error)
            },
          )
        })
      } catch (error) {
        console.log(JSON.stringify(error))
        log(`${serviceProvider} isAvailable error: ${JSON.stringify(error)}`)
      }
      // console.log(`AFTER HC check | isHealthConnectAvailable: ${isHealthConnectAvailable ? 'yes' : 'no'}`)
      if (!isHealthConnectAvailable) {
        const { value: consent } = await Dialog.confirm({
          title: t`Health Connect missing`,
          message: t`To use Health Connect, you need to upgrade to Android 14 or download the Health Connect app.`,
          okButtonTitle: t`Download`,
          cancelButtonTitle: t`Cancel`,
        })
        if (consent) {
          health.getHealthConnectFromStore()
        }
        return
      }
    }

    // Filter out data types that don't apply on each platform
    if (isGoogleFit) {
      // If we do not have location read scope (or are in `nonDistanceSync`), skip distance activity
      const userIntegrationScopes = getUserIntegrationScopes(state)
      if (nonDistanceSync || !userIntegrationScopes.includes('https://www.googleapis.com/auth/fitness.location.read')) {
        log(`Skipping distance | userIntegrationScopes: ${JSON.stringify(userIntegrationScopes)}`)
        healthDataTypes = healthDataTypes.filter((h) => h.activityType !== ActivityType.DISTANCE)
      }
    }

    // For some, do a "lean sync" (only steps/distance) if we don't have user's DOB
    if (!needsFullScopeSync) {
      healthDataTypes = healthDataTypes.filter((h) => !h.fullScope)
    }

    // Check user companies for features
    const userCompanies = getUserCompanies(state)
    const syncActivities = hasFeatureFlag(userCompanies, 'syncActivities')
    const syncSleep = hasFeatureFlag(userCompanies, 'sleepSync')

    // Without "syncActivities" feature flag, skip exercise time
    if (!syncActivities) {
      healthDataTypes = healthDataTypes.filter((h) => h.activityType !== ActivityType.EXERCISE_MIN)
    }
    // Without "syncSleep" feature flag, skip sleep time
    // Also skip sleep for Google Fit in this code
    if (!syncSleep || isGoogleFit) {
      healthDataTypes = healthDataTypes.filter((h) => h.activityType !== ActivityType.SLEEP_MIN)
    }

    // Build the scope object for requesting authorization
    const readPermissions = healthDataTypes.map((i) => i.dataType)
    const scope = [{ read: readPermissions }]

    // Request authorization from plugin
    await requestHealthAuthorization(health, scope, isGoogleFit, nonDistanceSync, serviceProvider, forceLongSync)

    // Get lastSyncDate per activity from the back-end
    const { lastSyncDatePerActivity }: any = isAppleHealth
      ? await api.activity.getAppleHealthSyncTimes({ forceLongSync })
      : isHealthConnect
        ? await api.activity.getHealthConnectSyncTimes({ forceLongSync })
        : await api.activity.getGoogleFitAndroidSyncTimes({ forceLongSync })

    if (lastSyncDatePerActivity === null) {
      return log(`${serviceProvider} sync DISABLED`)
    }

    document.body.classList.add('is-syncing')
    const combinedActivitiesData: {
      activityType: ActivityType
      diaryEntries: AppleHealthDiaryEntry[]
    }[] = []
    const promises: Promise<unknown>[] = []

    // Remove "workouts", we deal with them separately
    healthDataTypes = healthDataTypes.filter((h) => h.activityType)

    // Query each data type
    for (const { dataType, activityType, aggregated } of healthDataTypes) {
      if (!activityType) continue
      const startDate = lastSyncDatePerActivity[activityType as keyof typeof lastSyncDatePerActivity] as number
      if (!startDate) continue

      // Give some breathing room for Health Connect...
      if (isHealthConnect) {
        await new Promise((f) => setTimeout(f, 10))
      }

      let promise
      if (aggregated) {
        // Steps, exercise time, distance, and calories
        promise = syncAggregatedActivityData(
          health,
          dataType,
          activityType,
          startDate,
          isGoogleFit,
          isHealthConnect,
          serviceProvider,
          combinedActivitiesData,
          allowManualEntry,
        )
      } else {
        // Heart rate, height, weight, (and possibly sleep if not aggregated)
        promise = syncDiscreteActivityData(
          health,
          dataType,
          activityType,
          startDate,
          isGoogleFit,
          isHealthConnect,
          serviceProvider,
          combinedActivitiesData,
          allowManualEntry,
        )
      }

      // Apple Health can do it in parallel, Health Connect can't.
      // TODO: But didn't Google Fit can too?
      if (isAppleHealth) {
        promises.push(promise)
      }
      // iOS (Apple Health)
      else {
        await promise
      }
    }

    // Activity entries (full user activities/workouts)
    const activityEntries: HistoryActivityData[] = []
    let deletedActivities: any[] = []

    // Sync more detailed "Activity" if feature-flag is on
    const activityPromise = syncDetailedActivityData(
      syncActivities,
      lastSyncDatePerActivity[ProgressType.ACTIVITY],
      isGoogleFit,
      isAppleHealth,
      health,
      serviceProvider,
      activityEntries,
      allowManualEntry,
      promises,
    )

    // Health Connect does not support parallelization right now
    if (isHealthConnect) {
      await activityPromise

      try {
        const activityType = 'DELETED_ACTIVITIES'
        const delActSyncStart = window.performance.now()
        log(`${activityType} query starting | ${serviceProvider}`)
        const result = await HealthConnect.getDeletedActivities()
        const syncDuration = Math.round(window.performance.now() - delActSyncStart)
        const delActSize = result.deletedIds ? result.deletedIds.length : 0
        log(`${activityType} query (${delActSize} items) completed in ${syncDuration}ms | ${serviceProvider}`)
        if (delActSize > 0) {
          deletedActivities = result.deletedIds
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      promises.push(activityPromise)

      // Deleted activities if Apple Health
      if (isAppleHealth) {
        const delPromise = new Promise<void>((resolve) => {
          health.query(
            { dataType: 'deletedActivity' },
            (entries: any[]) => {
              log(`Found ${entries.length} deleted activities | ${serviceProvider}`)
              deletedActivities.push(...entries)
              resolve()
            },
            (error: unknown) => {
              console.log(error)
              resolve()
            },
          )
        })
        promises?.push(delPromise)
      }

      // Do all requests in parallel
      await Promise.all(promises)
    }

    // Sort so the largest sets (e.g., heart rate) come last
    combinedActivitiesData.sort((a, b) => (a.activityType > b.activityType ? -1 : 1))

    const queryDuration = Math.round((window.performance.now() - syncStart) / 1000)
    log(`${serviceProvider} queries completed in ${queryDuration} seconds.`)

    // Upload to server
    if (isGoogleFit) {
      await api.activity.uploadAllGoogleFitActivities({
        googleFitActivities: combinedActivitiesData as any,
        activityEntries,
        timeZone: dayjs.tz.guess(),
      })
    } else if (isHealthConnect) {
      await api.activity.uploadAllHealthConnectActivities({
        healthConnectActivities: combinedActivitiesData as any,
        activityEntries,
        deletedActivities,
        timeZone: dayjs.tz.guess(),
      })
    } else {
      await api.activity.uploadAllAppleHealthActivities({
        appleHealthActivities: combinedActivitiesData as any,
        activityEntries,
        deletedActivities,
        timeZone: dayjs.tz.guess(),
      })
    }

    // On successful sync, initialize changes token.
    /*if (isHealthConnect) {
      await HealthConnect.initializeChangesToken()
    }*/

    // Give backend some time to recalculate, then refresh
    setTimeout(refreshActivityView, 250)

    const syncDuration = Math.round((window.performance.now() - syncStart) / 1000)
    log(`${serviceProvider} sync completed in ${syncDuration} seconds.`)

    // Remove the syncing class with a little spinner iteration trick
    const icon = document.querySelector('.btn-sync .button__icon')
    if (icon) {
      icon.addEventListener(
        'animationiteration',
        () => {
          document.body.classList.remove('is-syncing')
        },
        { once: true },
      )
    }
  } catch (error: unknown) {
    console.log('Error: ', error)
    document.body.classList.remove('is-syncing')
  }
}

/* --------------------------------- Helpers -------------------------------- */

/**
 * Request authorization from HealthKit / Google Fit / Health Connect.
 */
async function requestHealthAuthorization(
  health: any,
  scope: Array<{ read: string[] }>,
  isGoogleFit: boolean,
  nonDistanceSync: boolean | undefined,
  serviceProvider: ServiceProvider,
  forceLongSync: boolean,
): Promise<void> {
  if (isGoogleFit) {
    await GoogleFit.requestAuthorization({ scope }).catch((error: unknown) => {
      console.log(JSON.stringify(error))
      log(`${serviceProvider} sync requestAuthorization error: ${JSON.stringify(error)}`)
      if (!nonDistanceSync) {
        // Retry skipping distance
        dataSync(true, forceLongSync, true)
      }
    })
  } else {
    // iOS / HealthConnect - Apple HealthKit or cordova.plugins.health
    await new Promise<void>((resolve, reject) => {
      const authScope = { read: scope[0].read }
      console.log('authScope', authScope)
      health.requestAuthorization(
        authScope,
        () => resolve(),
        (error: unknown) => {
          console.log(JSON.stringify(error))
          log(`${serviceProvider} sync requestAuthorization error: ${JSON.stringify(error)}`)
          reject(error)
        },
      )
    })
  }
}

/**
 * Sync aggregated data (e.g., steps, distance, calories, exercise minutes).
 */
async function syncAggregatedActivityData(
  health: any,
  dataType: string,
  activityType: ActivityType,
  startDate: number,
  isGoogleFit: boolean,
  isHealthConnect: boolean,
  serviceProvider: ServiceProvider,
  combinedActivitiesData: {
    activityType: ActivityType
    diaryEntries: AppleHealthDiaryEntry[]
  }[],
  allowManualEntry?: boolean,
): Promise<void> {
  const indiSyncStart = window.performance.now()
  log(`${activityType} query starting | ${serviceProvider}`)

  const handleQueryResult = async (result: any) => {
    // For GoogleFit or HealthConnect, often returns { activities: [...] }
    let entries: AppleHealthDiaryEntry[] = isGoogleFit ? result.activities : result
    entries = entries || []

    // Remove zero-value items if not GoogleFit
    if (!isGoogleFit) {
      entries = entries.filter((entry) => entry.value > 0)
    }

    const mapped = entries.map((entry) => ({
      startDate: entry.startDate,
      endDate: entry.endDate,
      value: Math.floor(entry.value),
    }))

    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
    log(`${activityType} query (${mapped.length} items) completed in ${syncDuration}ms | ${serviceProvider}`)

    combinedActivitiesData.push({
      activityType,
      diaryEntries: mapped,
    })
  }

  try {
    if (isGoogleFit) {
      const result = await GoogleFit.queryAggregated({
        dataType,
        endDate: new Date().getTime(),
        startDate: convertUTCToLocalTime(startDate).getTime(),
        bucket: 'day',
        // This is pointless, because we can't do the same with the cloud sync.
        // filterOutUserInput: !allowManualEntry,
      })
      await handleQueryResult(result)
      return
    }

    /*if (isHealthConnect) {
      // Future placeholder for HealthConnect aggregated calls
      // If you have an aggregated function, do it here
      // E.g.:
      // const result = await HealthConnect.queryAggregated(...)
      // handleQueryResult(result)
      return new Promise<void>((resolve) => resolve())
    }*/

    return new Promise<void>((resolve) => {
      let theSyncStart = convertUTCToLocalTime(startDate)
      // Example: clamp earliest sleep entries
      const earliestSleepSync = dayjs.utc('2025-02-01').toDate()
      //const earliestSleepSync = moment('2025-01-01').utc().toDate()
      if (activityType === ActivityType.SLEEP_MIN && theSyncStart < earliestSleepSync) {
        theSyncStart = earliestSleepSync
      }

      health.queryAggregated(
        {
          dataType,
          endDate: isHealthConnect ? dayjs().endOf('day').toDate() : new Date(),
          startDate: theSyncStart,
          bucket: 'day',
          filterOutUserInput: !allowManualEntry,
        },
        (result: any) => {
          handleQueryResult(result)
          resolve()
        },
        (error: unknown) => {
          console.log(error)
          const syncDuration = Math.round(window.performance.now() - indiSyncStart)
          log(`${activityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
          resolve()
        },
      )
    })
  } catch (error) {
    console.log(error)
    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
    log(`${activityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
    return
  }
}

/**
 * Sync discrete data (e.g., heart rate, height, weight, or non-aggregated data).
 */
async function syncDiscreteActivityData(
  health: any,
  dataType: string,
  activityType: ActivityType,
  startDate: number,
  isGoogleFit: boolean,
  isHealthConnect: boolean,
  serviceProvider: ServiceProvider,
  combinedActivitiesData: {
    activityType: ActivityType
    diaryEntries: AppleHealthDiaryEntry[]
  }[],
  allowManualEntry?: boolean,
): Promise<void> {
  const indiSyncStart = window.performance.now()
  log(`${activityType} query starting | ${serviceProvider}`)

  const handleQueryResult = async (result: any) => {
    // For GoogleFit or HealthConnect, possibly returns { activities: [...] }
    let entries: AppleHealthDiaryEntry[] = isGoogleFit || isHealthConnect ? result.activities || result : result
    entries = entries || []

    const mapped = entries.map((entry) => ({
      startDate: entry.startDate,
      endDate: entry.endDate,
      value: entry.value,
    }))

    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
    log(`${activityType} query (${mapped.length} items) completed in ${syncDuration}ms | ${serviceProvider}`)

    // If combinedActivitiesData already has this activityType, append;
    // otherwise push a new entry
    const existing = combinedActivitiesData.find((act) => act.activityType === activityType)
    if (existing) {
      existing.diaryEntries = existing.diaryEntries.concat(mapped)
    } else {
      combinedActivitiesData.push({ activityType, diaryEntries: mapped })
    }
  }

  try {
    // Google Fit special calls for heart rate or sleep
    if (isGoogleFit) {
      if (activityType === ActivityType.HEART_RATE) {
        const result = await GoogleFit.heartRateQuery({
          startTime: convertUTCToLocalTime(startDate).getTime(),
          endTime: new Date().getTime(),
          // This is pointless, because we can't do the same with the cloud sync.
          // filterOutUserInput: !allowManualEntry,
        })
        await handleQueryResult(result)
      } else if (activityType === ActivityType.SLEEP_MIN) {
        const result = await GoogleFit.getSleepActivity({
          startTime: convertUTCToLocalTime(startDate).getTime(),
          endTime: new Date().getTime(),
        })
        await handleQueryResult(result)
      }
      return
    }

    // HealthConnect special logic for daily queries
    if (isHealthConnect && activityType === ActivityType.SLEEP_MIN) {
      const result = await HealthConnect.getSleepActivity({
        startTime: convertUTCToLocalTime(startDate).getTime(),
        endTime: new Date().getTime(),
      })
      await handleQueryResult(result)
      return
    }

    if (isHealthConnect && activityType === ActivityType.HEART_RATE) {
      log(`Starting Health Connect ${activityType} sync from ${startDate} to ${new Date().getTime()}`)
      const result = await HealthConnect.getHeartRate({
        startTime: convertUTCToLocalTime(startDate).getTime(),
        endTime: new Date().getTime(),
      })
      await handleQueryResult(result)
      return
    }

    // HealthConnect daily loop for other metrics, e.g. heart rate
    if (isHealthConnect) {
      let currentDay = new Date(startDate)
      const today = new Date()

      // Loop day by day
      while (currentDay <= today) {
        // Next day
        const nextDay = new Date(currentDay)
        nextDay.setDate(nextDay.getDate() + 1)

        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          log(`Starting Health Connect ${dataType} sync from ${currentDay} to ${nextDay}`)
          health.query(
            {
              dataType,
              startDate: currentDay,
              endDate: nextDay,
              limit: 5000,
            },
            (res: any) => {
              log(`Completed Health Connect ${dataType} sync from ${currentDay} to ${nextDay}`)
              handleQueryResult(res)
              resolve()
            },
            (error: unknown) => {
              log(`Failed Health Connect ${dataType} sync from ${currentDay} to ${nextDay} | error: ${error}`)
              console.error(error)
              resolve()
            },
          )
        })
        currentDay = nextDay
      }
      return
    }

    // General discrete query
    return new Promise<void>((resolve) => {
      health.query(
        {
          dataType,
          endDate: new Date(),
          startDate: new Date(startDate),
          filterOutUserInput: !allowManualEntry,
          limit: 300000,
        },
        (result: any) => {
          handleQueryResult(result)
          resolve()
        },
        (error: unknown) => {
          console.log(error)
          const syncDuration = Math.round(window.performance.now() - indiSyncStart)
          log(`${activityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
          resolve()
        },
      )
    })
  } catch (error) {
    console.log(error)
    const syncDuration = Math.round(window.performance.now() - indiSyncStart)
    log(`${activityType} query failed in ${syncDuration}ms | ${serviceProvider}`)
  }
}

/**
 * Sync more detailed "Activity" data (i.e., full workouts), plus handle deleted activities on Apple Health.
 */
async function syncDetailedActivityData(
  syncActivities: boolean,
  activityStartDate: number,
  isGoogleFit: boolean,
  isAppleHealth: boolean,
  health: any,
  serviceProvider: ServiceProvider,
  activityEntries: HistoryActivityData[],
  allowManualEntry?: boolean,
  promises?: Promise<unknown>[],
): Promise<void> {
  if (!syncActivities || !activityStartDate) return

  const indiSyncStart = window.performance.now()
  const activityActivityType = ProgressType.ACTIVITY
  log(`${activityActivityType} query starting | ${serviceProvider}`)

  if (isGoogleFit) {
    try {
      const { activities } = (await health.getHistoryActivity({
        startTime: convertUTCToLocalTime(activityStartDate).getTime(),
        endTime: new Date().getTime(),
        // This is pointless, because we can't do the same with the cloud sync.
        // filterOutUserInput: !allowManualEntry,
      })) as ActivityContainer

      const entries = activities.map((entry) => ({
        startDate: entry.start,
        endDate: entry.end,
        activityType: entry.activity,
        calories: entry.calories,
        distance: entry.distance,
        duration: entry.duration,
        steps: entry.steps || 0,
        source: entry.source,
        originalId: entry.id,
      }))

      const syncDuration = Math.round(window.performance.now() - indiSyncStart)
      log(`${activityActivityType} query (${entries.length} items) completed in ${syncDuration}ms | ${serviceProvider}`)
      activityEntries.push(...(entries as any))
    } catch (error) {
      console.log(error)
    }
  } else {
    // Apple Health or HealthConnect
    return new Promise<void>((resolve) => {
      health.query(
        {
          dataType: 'activity',
          endDate: new Date().getTime(),
          startDate: convertUTCToLocalTime(activityStartDate).getTime(),
          limit: 1000,
          includeDistance: true,
          // This would need extra permissions, and we don't actually use it
          includeCalories: false,
          filterOutUserInput: !allowManualEntry,
        },
        (entries: any[]) => {
          const mapped = entries.map((entry) => {
            let duration = entry.duration
            // Health Connect does not seem to have calculated duration.
            if (!duration && entry.startDate && entry.endDate) {
              duration = calculateDurationInSeconds(entry.startDate, entry.endDate)
              // console.log('duration', duration, entry.startDate, entry.endDate)
            }
            return {
              startDate: entry.startDate,
              endDate: entry.endDate,
              activityType: entry.value,
              calories: entry.calories,
              distance: entry.distance,
              duration,
              steps: 0,
              source: `${entry.sourceBundleId} (${entry.sourceName?.replace(/\u00a0/g, ' ')})`,
              originalId: entry.id,
            }
          })
          // console.log('mapped', mapped)

          const syncDuration = Math.round(window.performance.now() - indiSyncStart)
          log(
            `${activityActivityType} query (${mapped.length} items) completed in ${syncDuration}ms | ${serviceProvider}`,
          )
          activityEntries.push(...(mapped as any))
          resolve()
        },
        (error: unknown) => {
          console.log(error)
          resolve()
        },
      )
    })
  }
}
